/**
 * Numerical Inverse Kinematics for Universal Robots
 *
 * Uses Damped Least Squares (DLS) method for robust IK solving
 * This is more reliable than analytical methods and works for all UR robot configurations
 */

import { forwardKinematics } from './URForwardKinematics.js';

const MAX_ITERATIONS = 100;
const POSITION_TOLERANCE = 0.0001; // 0.1mm
const ROTATION_TOLERANCE = 0.001; // ~0.057 degrees
const DAMPING_FACTOR = 0.1;
const STEP_SIZE = 0.5;

/**
 * Compute numerical inverse kinematics using Damped Least Squares
 * @param {Object} targetPose - Target pose
 * @param {Object} dh - DH parameters
 * @param {Array<number>} initialJoints - Initial guess for joint angles
 * @returns {Array<number>|null} Joint solution or null if failed to converge
 */
export function inverseKinematicsNumerical(targetPose, dh, initialJoints = null) {
  // Initialize with zero configuration if no initial guess
  let joints = initialJoints || [0, -Math.PI / 4, -Math.PI / 4, 0, Math.PI / 2, 0];

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    // Compute current end-effector pose
    const currentPose = forwardKinematics(joints, dh);

    // Calculate error
    const posError = {
      x: targetPose.position.x - currentPose.position.x,
      y: targetPose.position.y - currentPose.position.y,
      z: targetPose.position.z - currentPose.position.z,
    };

    const rotError = {
      rx: targetPose.rotation.rx - currentPose.rotation.rx,
      ry: targetPose.rotation.ry - currentPose.rotation.ry,
      rz: targetPose.rotation.rz - currentPose.rotation.rz,
    };

    // Check convergence
    const posErrorMag = Math.sqrt(posError.x ** 2 + posError.y ** 2 + posError.z ** 2);
    const rotErrorMag = Math.sqrt(rotError.rx ** 2 + rotError.ry ** 2 + rotError.rz ** 2);

    if (posErrorMag < POSITION_TOLERANCE && rotErrorMag < ROTATION_TOLERANCE) {
      // Converged!
      return joints;
    }

    // Compute Jacobian matrix numerically
    const J = computeNumericalJacobian(joints, dh);

    // Create error vector [dx, dy, dz, drx, dry, drz]
    const error = [posError.x, posError.y, posError.z, rotError.rx, rotError.ry, rotError.rz];

    // Compute joint update using Damped Least Squares
    // Δθ = JT * (J * JT + λ²I)^(-1) * error
    const deltaJoints = dampedLeastSquares(J, error, DAMPING_FACTOR);

    // Update joints with step size limiter
    for (let i = 0; i < 6; i++) {
      joints[i] += deltaJoints[i] * STEP_SIZE;
      // Normalize to [-π, π]
      joints[i] = normalizeAngle(joints[i]);
    }
  }

  // Failed to converge
  return null;
}

/**
 * Compute numerical Jacobian matrix
 * @param {Array<number>} joints - Current joint angles
 * @param {Object} dh - DH parameters
 * @returns {Array<Array<number>>} 6x6 Jacobian matrix
 */
function computeNumericalJacobian(joints, dh) {
  const J = Array(6).fill(0).map(() => Array(6).fill(0));
  const delta = 0.0001; // Small perturbation

  // Get current pose
  const currentPose = forwardKinematics(joints, dh);

  // Compute partial derivatives for each joint
  for (let i = 0; i < 6; i++) {
    const perturbedJoints = [...joints];
    perturbedJoints[i] += delta;

    const perturbedPose = forwardKinematics(perturbedJoints, dh);

    // Position derivatives
    J[0][i] = (perturbedPose.position.x - currentPose.position.x) / delta;
    J[1][i] = (perturbedPose.position.y - currentPose.position.y) / delta;
    J[2][i] = (perturbedPose.position.z - currentPose.position.z) / delta;

    // Rotation derivatives
    J[3][i] = (perturbedPose.rotation.rx - currentPose.rotation.rx) / delta;
    J[4][i] = (perturbedPose.rotation.ry - currentPose.rotation.ry) / delta;
    J[5][i] = (perturbedPose.rotation.rz - currentPose.rotation.rz) / delta;
  }

  return J;
}

/**
 * Solve using Damped Least Squares method
 * Δθ = JT * (J * JT + λ²I)^(-1) * error
 * @param {Array<Array<number>>} J - Jacobian matrix (6x6)
 * @param {Array<number>} error - Error vector (6x1)
 * @param {number} lambda - Damping factor
 * @returns {Array<number>} Joint update vector (6x1)
 */
function dampedLeastSquares(J, error, lambda) {
  // Compute J * JT
  const JJT = multiplyMatrices(J, transposeMatrix(J));

  // Add damping: J * JT + λ²I
  for (let i = 0; i < 6; i++) {
    JJT[i][i] += lambda * lambda;
  }

  // Invert (J * JT + λ²I)
  const JJT_inv = invertMatrix6x6(JJT);

  // Compute JT * (J * JT + λ²I)^(-1)
  const JT_JJT_inv = multiplyMatrices(transposeMatrix(J), JJT_inv);

  // Multiply by error vector
  const deltaJoints = Array(6).fill(0);
  for (let i = 0; i < 6; i++) {
    for (let j = 0; j < 6; j++) {
      deltaJoints[i] += JT_JJT_inv[i][j] * error[j];
    }
  }

  return deltaJoints;
}

/**
 * Transpose a matrix
 */
function transposeMatrix(M) {
  const rows = M.length;
  const cols = M[0].length;
  const MT = Array(cols).fill(0).map(() => Array(rows).fill(0));

  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      MT[j][i] = M[i][j];
    }
  }

  return MT;
}

/**
 * Multiply two matrices
 */
function multiplyMatrices(A, B) {
  const rowsA = A.length;
  const colsA = A[0].length;
  const colsB = B[0].length;

  const result = Array(rowsA).fill(0).map(() => Array(colsB).fill(0));

  for (let i = 0; i < rowsA; i++) {
    for (let j = 0; j < colsB; j++) {
      for (let k = 0; k < colsA; k++) {
        result[i][j] += A[i][k] * B[k][j];
      }
    }
  }

  return result;
}

/**
 * Invert a 6x6 matrix using Gaussian elimination
 */
function invertMatrix6x6(M) {
  const n = 6;
  // Create augmented matrix [M | I]
  const aug = M.map((row, i) => [...row, ...Array(n).fill(0).map((_, j) => (i === j ? 1 : 0))]);

  // Forward elimination with partial pivoting
  for (let i = 0; i < n; i++) {
    // Find pivot
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(aug[k][i]) > Math.abs(aug[maxRow][i])) {
        maxRow = k;
      }
    }

    // Swap rows
    [aug[i], aug[maxRow]] = [aug[maxRow], aug[i]];

    // Make diagonal 1
    const divisor = aug[i][i];
    if (Math.abs(divisor) < 1e-10) {
      throw new Error('Matrix is singular');
    }

    for (let j = 0; j < 2 * n; j++) {
      aug[i][j] /= divisor;
    }

    // Eliminate column
    for (let k = 0; k < n; k++) {
      if (k !== i) {
        const factor = aug[k][i];
        for (let j = 0; j < 2 * n; j++) {
          aug[k][j] -= factor * aug[i][j];
        }
      }
    }
  }

  // Extract inverse from augmented matrix
  return aug.map(row => row.slice(n));
}

/**
 * Normalize angle to [-π, π]
 */
function normalizeAngle(angle) {
  let normalized = angle % (2 * Math.PI);
  if (normalized > Math.PI) {
    normalized -= 2 * Math.PI;
  } else if (normalized < -Math.PI) {
    normalized += 2 * Math.PI;
  }
  return normalized;
}

/**
 * Generate multiple IK solutions by trying different initial guesses
 * @param {Object} targetPose - Target pose
 * @param {Object} dh - DH parameters
 * @param {Array<number>} currentJoints - Current joint configuration (used as one initial guess)
 * @returns {Array<Array<number>>} Array of valid IK solutions
 */
export function inverseKinematicsMultipleSolutions(targetPose, dh, currentJoints = null) {
  const solutions = [];
  const solutionHashes = new Set();

  // Initial guess configurations to try
  const initialGuesses = [
    currentJoints || [0, -Math.PI / 4, -Math.PI / 4, 0, Math.PI / 2, 0], // Current or default
    [0, -Math.PI / 6, -Math.PI / 3, 0, Math.PI / 2, 0], // Elbow up
    [Math.PI, -Math.PI / 6, Math.PI / 3, Math.PI, Math.PI / 2, 0], // Elbow down, flipped
    [Math.PI / 2, -Math.PI / 4, -Math.PI / 4, 0, Math.PI / 2, 0], // Rotated 90°
    [-Math.PI / 2, -Math.PI / 4, -Math.PI / 4, 0, Math.PI / 2, 0], // Rotated -90°
  ];

  for (const guess of initialGuesses) {
    const solution = inverseKinematicsNumerical(targetPose, dh, guess);

    if (solution) {
      // Create a hash to check for duplicate solutions
      const hash = solution.map(j => Math.round(j * 1000)).join(',');

      if (!solutionHashes.has(hash)) {
        solutionHashes.add(hash);
        solutions.push(solution);
      }
    }
  }

  return solutions;
}
